import React from 'react';
import { useEffect, useState } from "react";
import AppConst from "../apis/appconst";
import axios from "axios";
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

function GSTReports() {
    const [formData, setFormData] = useState({
        saleGstList: [],
        purchaseGstList: [],
        search_key: '',
        from_date: new Date().toISOString().split('T')[0],
        to_date: new Date().toISOString().split('T')[0],
        activeTab: 'sale' // 'sale' or 'purchase'
    });

    const [filteredData, setFilteredData] = useState([]);

    const initData = async () => {
        const storedData = sessionStorage.getItem("ldata");

        if (storedData) {
            const fd = new FormData();
            fd.append('from_date', formData.from_date);
            fd.append('to_date', formData.to_date);

            try {
                const response = await axios.post(
                    AppConst.BASE_URL + AppConst.getGstReportUrl, 
                    fd, 
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );
                
                if (response.data.status) {
                    setFormData(prev => ({
                        ...prev,
                        saleGstList: response.data.sale_records || [],
                        purchaseGstList: response.data.purchase_records || []
                    }));
                } else {
                    toast.error(response.data.message || "Failed to fetch GST data");
                }
            } catch (error) {
                console.error(error);
                toast.error("Error fetching GST reports");
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleClearFilters = () => {
        const today = new Date().toISOString().split('T')[0];
        setFormData(prev => ({
            ...prev,
            search_key: "",
            from_date: today,
            to_date: today,
        }));
    };

    const handleTabChange = (tab) => {
        setFormData(prev => ({
            ...prev,
            activeTab: tab,
            search_key: '' // Clear search when switching tabs
        }));
    };

    // Filter data based on search and active tab
    useEffect(() => {
        const currentList = formData.activeTab === 'sale' 
            ? formData.saleGstList 
            : formData.purchaseGstList;

        if (formData.search_key.trim() === '') {
            setFilteredData(currentList);
        } else {
            const searchLower = formData.search_key.toLowerCase();
            const filtered = currentList.filter(item => 
                item.medicine_name?.toLowerCase().includes(searchLower) ||
                item.supplier_name?.toLowerCase().includes(searchLower) ||
                item.batch_no?.toLowerCase().includes(searchLower)
            );
            setFilteredData(filtered);
        }
    }, [formData.search_key, formData.saleGstList, formData.purchaseGstList, formData.activeTab]);

    useEffect(() => {
        initData();
    }, []);

    // Auto-reload when dates change
    useEffect(() => {
        if (formData.from_date && formData.to_date) {
            initData();
        }
    }, [formData.from_date, formData.to_date]);

    // Calculate totals
    const calculateTotals = () => {
        let totalGst = 0;
        let totalMrp = 0;

        filteredData.forEach(item => {
            totalGst += parseFloat(item.gst_amount || 0);
            totalMrp += parseFloat(item.mrp || 0);
        });

        return { totalGst, totalMrp };
    };

    const totals = calculateTotals();

    // Export to PDF
    const exportToPDF = () => {
        const doc = new jsPDF('l', 'mm', 'a4');
        
        const reportType = formData.activeTab === 'sale' ? 'Sale GST Report' : 'Purchase GST Report';
        
        // Add title
        doc.setFontSize(18);
        doc.text(reportType, 14, 15);
        
        // Add date range
        doc.setFontSize(10);
        doc.text(`Date Range: ${formData.from_date} to ${formData.to_date}`, 14, 22);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 27);
        
        // Prepare table data
        const tableData = filteredData.map((item, index) => [
            index + 1,
            formData.activeTab === 'sale' ? (item.bill_no || '-') : '-',
            formData.activeTab === 'sale' ? (item.med_bill_ipd_number || '-') : '-',
            item.medicine_name || '-',
            item.supplier_name || '-',
            `${parseFloat(item.mrp || 0).toFixed(2)}`,
            `${parseFloat(item.gst_amount || 0).toFixed(2)}`
        ]);

        // Add table
        autoTable(doc, {
            //head: [['Sl No', 'Medicine Name', formData.activeTab === 'sale' ? 'Customer Name' : 'Supplier Name', 'MRP', 'GST Amount']],
            
            head: [[
                'Sl No',
                'Bill No',
                'UHID No',
                'Medicine Name',
                formData.activeTab === 'sale' ? 'Customer Name' : 'Supplier Name',
                'MRP',
                'GST Amount'
            ]],
            body: tableData,
            startY: 32,
            theme: 'striped',
            headStyles: { fillColor: [64, 81, 137], textColor: 255, fontSize: 9 },
            bodyStyles: { fontSize: 8 },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            columnStyles: {
                0: { cellWidth: 15 },
                1: { cellWidth: 22 },
                2: { cellWidth: 35 },
                3: { cellWidth: 65 },
                4: { cellWidth: 55 },
                5: { cellWidth: 30, halign: 'right' },
                6: { cellWidth: 30, halign: 'right' }
            },
            foot: [[
                'Total',
                '',
                '',
                `${totals.totalMrp.toFixed(2)}`,
                `${totals.totalGst.toFixed(2)}`
            ]],
            footStyles: {
                fillColor: [64, 81, 137],
                textColor: 255,
                fontStyle: 'bold',
                halign: 'right'
            }
        });

        // Add footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.text(
                `Page ${i} of ${pageCount}`,
                doc.internal.pageSize.getWidth() / 2,
                doc.internal.pageSize.getHeight() - 10,
                { align: 'center' }
            );
        }

        const fileName = formData.activeTab === 'sale' 
            ? `Sale_GST_Report_${formData.from_date}_to_${formData.to_date}.pdf`
            : `Purchase_GST_Report_${formData.from_date}_to_${formData.to_date}.pdf`;

        doc.save(fileName);
        toast.success("PDF exported successfully!");
    };

    // DataTable Columns
    const columns = [
        {
            name: 'Sl No',
            selector: (row, index) => index + 1,
            sortable: false,
            width: '80px',
            center: true
        },

        {
            name: 'Bill No',
            selector: row => row.bill_no || '-',
            sortable: true,
            width: '110px',
            center: true
        },
        {
            name: 'UHID No',
            selector: row => row.med_bill_ipd_number || '-',
            sortable: true,
            width: '150px',
            wrap: true
        },
        {
            name: 'Medicine Name',
            selector: row => row.medicine_name,
            sortable: true,
            grow: 3,
            wrap: true
        },
        {
            name: formData.activeTab === 'sale' ? 'Customer Name' : 'Supplier Name',
            selector: row =>
                formData.activeTab === 'sale'
                    ? row.supplier_name || row.party_name || '-'   // SALE
                    : row.supplier_name || row.party_name || '-', // PURCHASE
            sortable: true,
            grow: 2,
            wrap: true
        },
        
        {
            name: 'MRP',
            selector: row => row.mrp,
            sortable: true,
            width: '120px',
            right: true,
            cell: row => `₹${parseFloat(row.mrp || 0).toFixed(2)}`
        },
        {
            name: 'GST Amount',
            selector: row => row.gst_amount,
            sortable: true,
            width: '130px',
            right: true,
            cell: row => (
                <span className="badge bg-success-subtle text-success fs-6">
                    ₹{parseFloat(row.gst_amount || 0).toFixed(2)}
                </span>
            )
        }
    ];

    // Custom styles for DataTable
    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#405189',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 'bold',
                borderRadius: '5px 5px 0 0'
            }
        },
        headCells: {
            style: {
                color: '#fff',
                paddingLeft: '16px',
                paddingRight: '16px'
            }
        },
        rows: {
            style: {
                fontSize: '14px',
                '&:hover': {
                    backgroundColor: '#f8f9fa',
                }
            }
        }
    };

    return (
        <div className="page-content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="card">
                            <div className="card-header border-0">
                                <div className="row align-items-center gy-3">
                                    <div className="col-sm">
                                        <h5 className="card-title mb-0">
                                            <i className="ri-file-chart-line me-2"></i>
                                            GST Reports
                                        </h5>
                                    </div>
                                    <div className="col-sm-auto">
                                        <button 
                                            type="button" 
                                            className="btn btn-danger"
                                            onClick={exportToPDF}
                                            disabled={filteredData.length === 0}
                                        >
                                            <i className="ri-file-pdf-line align-bottom me-1"></i> 
                                            Export PDF
                                        </button>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Tab Buttons */}
                            <div className="card-body border-bottom">
                                <div className="btn-group w-100" role="group">
                                    <button
                                        type="button"
                                        className={`btn ${formData.activeTab === 'sale' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => handleTabChange('sale')}
                                    >
                                        <i className="ri-shopping-cart-line me-1"></i>
                                        Sale GST
                                        <span className="badge bg-light text-dark ms-2">
                                            {formData.saleGstList.length}
                                        </span>
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn ${formData.activeTab === 'purchase' ? 'btn-success' : 'btn-outline-success'}`}
                                        onClick={() => handleTabChange('purchase')}
                                    >
                                        <i className="ri-shopping-bag-line me-1"></i>
                                        Purchase GST
                                        <span className="badge bg-light text-dark ms-2">
                                            {formData.purchaseGstList.length}
                                        </span>
                                    </button>
                                </div>
                            </div>

                            <div className="card-body border border-dashed border-end-0 border-start-0">
                                <div className="row g-3">
                                    <div className="col-md-3">
                                        <label className="form-label">From Date</label>
                                        <input 
                                            className="form-control" 
                                            type="date" 
                                            name="from_date"
                                            onChange={handleChange} 
                                            value={formData.from_date} 
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">To Date</label>
                                        <input 
                                            className="form-control" 
                                            type="date" 
                                            name="to_date"
                                            onChange={handleChange} 
                                            value={formData.to_date} 
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label">&nbsp;</label>
                                        <div>
                                            <button 
                                                type="button" 
                                                onClick={handleClearFilters} 
                                                className="btn btn-info w-100"
                                            >
                                                <i className="ri-refresh-line me-1"></i> Clear
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Search</label>
                                        <input 
                                            className="form-control" 
                                            placeholder="Search by Medicine, Supplier or Batch..." 
                                            type="text"
                                            name="search_key" 
                                            onChange={handleChange} 
                                            value={formData.search_key} 
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="card-body pt-0">
                                <DataTable
                                    columns={columns}
                                    data={filteredData}
                                    customStyles={customStyles}
                                    pagination
                                    paginationPerPage={25}
                                    paginationRowsPerPageOptions={[10, 25, 50, 100, 200]}
                                    highlightOnHover
                                    responsive
                                    striped
                                    dense
                                    noDataComponent={
                                        <div className="text-center py-5">
                                            <i className="ri-inbox-line fs-1 text-muted"></i>
                                            <p className="mt-3">
                                                No {formData.activeTab === 'sale' ? 'sale' : 'purchase'} GST records found
                                            </p>
                                        </div>
                                    }
                                />
                                
                                <div className="mt-3 p-3 bg-light rounded">
                                    <div className="row text-center">
                                        <div className="col-md-4">
                                            <strong>Total Records:</strong>
                                            <div className="fs-5 text-primary">{filteredData.length}</div>
                                        </div>
                                        <div className="col-md-4">
                                            <strong>Total MRP:</strong>
                                            <div className="fs-5 text-info">₹ {totals.totalMrp.toFixed(2)}</div>
                                        </div>
                                        <div className="col-md-4">
                                            <strong>Total GST Amount:</strong>
                                            <div className="fs-5 text-success">₹ {totals.totalGst.toFixed(2)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default GSTReports;